import React, { useState, useContext, useMemo, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { SpacingProps } from '@mui/system';
import EDChat, { IChatMessage } from '../../BaseComponents/Chat/EDChat';
import { ActiveDatabaseContext } from '../ActiveDatabaseContext/ActiveDatabaseContext';
import SYSAIChattInitMutation from './Mutations/SYSAIChattInitMutation.gql';
import SYSAIChattContinueMutation from './Mutations/SYSAIChattContinueMutation.gql';
import {
  SYSAIChattInit,
  SYSAIChattInitVariables,
} from './Mutations/_QueryTypes/SYSAIChattInit';
import {
  SYSAIChattContinue,
  SYSAIChattContinueVariables,
} from './Mutations/_QueryTypes/SYSAIChattContinue';

export interface IProps extends SpacingProps {
  contextData?: string;
  placeholder?: string;
  disabled?: boolean;
  height?: number | string;
  testId?: string;
}

interface IChatSession {
  id: string;
  title?: string;
}

export const EDAIChat: React.FC<IProps> = props => {
  const { activeDatabase } = useContext(ActiveDatabaseContext);
  const [messages, setMessages] = useState<IChatMessage[]>([]);
  const [chatSession, setChatSession] = useState<IChatSession | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [initChatMutation] = useMutation<
    SYSAIChattInit,
    SYSAIChattInitVariables
  >(SYSAIChattInitMutation);

  const [continueChatMutation] = useMutation<
    SYSAIChattContinue,
    SYSAIChattContinueVariables
  >(SYSAIChattContinueMutation);

  const convertMessagesToChat = useCallback(
    (chatData: any, debug = false): IChatMessage[] => {
      if (!chatData?._Associations?.Meddelanden_All) return [];

      return chatData._Associations.Meddelanden_All.map(
        (msg: any, index: number) => ({
          id: msg.id || `msg-${index}`,
          text:
            (debug ? msg.INNEHALL : msg.MEDDELANDE) || msg.FELMEDDELANDE || '',
          sender: msg.ROLL === 'U' ? 'user' : 'ai',
        })
      );
    },
    []
  );

  const handleSendMessage = useCallback(
    async (message: string) => {
      if (!activeDatabase || isLoading) return;

      setIsLoading(true);

      const userMessage: IChatMessage = {
        id: `user-${Date.now()}`,
        text: message,
        sender: 'user',
      };

      setMessages(prev => [...prev, userMessage]);

      try {
        if (!chatSession) {
          const result = await initChatMutation({
            variables: {
              dbname: activeDatabase,
              USERMESSAGE: message,
              CONTEXTDATA: props.contextData || '',
            },
          });

          if (result.data?.rtn?.SYSAIChattInit) {
            const chatData = result.data.rtn.SYSAIChattInit;
            setChatSession({
              id: chatData.id,
              title: chatData.TITEL,
            });

            const allMessages = convertMessagesToChat(chatData);
            setMessages(allMessages);
          }
        } else {
          const result = await continueChatMutation({
            variables: {
              dbname: activeDatabase,
              CHATID: chatSession.id,
              USERMESSAGE: message,
            },
          });

          if (result.data?.rtn?.SYSAIChattContinue) {
            const chatData = result.data.rtn.SYSAIChattContinue;
            const allMessages = convertMessagesToChat(chatData);
            setMessages(allMessages);
          }
        }
      } catch (error) {
        // console.error('AI Chat error:', error);
        const errorMessage: IChatMessage = {
          id: `error-${Date.now()}`,
          text: 'Ett fel uppstod vid kommunikation med AI-chatten.',
          sender: 'ai',
        };
        setMessages(prev => [...prev, errorMessage]);
      } finally {
        setIsLoading(false);
      }
    },
    [
      activeDatabase,
      chatSession,
      initChatMutation,
      continueChatMutation,
      convertMessagesToChat,
      props.contextData,
      isLoading,
    ]
  );

  const chatProps = useMemo(
    () => ({
      messages,
      onSendMessage: handleSendMessage,
      placeholder: props.placeholder || 'Ställ en fråga...',
      disabled: props.disabled || isLoading || !activeDatabase,
      height: props.height,
      testId: props.testId,
    }),
    [
      messages,
      handleSendMessage,
      props.placeholder,
      props.disabled,
      props.height,
      props.testId,
      isLoading,
      activeDatabase,
    ]
  );

  return <EDChat {...chatProps} />;
};

export default EDAIChat;
